import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider
} from '@mui/material';
import { 
  CheckCircle as CheckIcon, 
  Warning as WarningIcon,
  CloudUpload as UploadIcon,
  Visibility as ViewIcon,
  Description as DocIcon,
  Verified as VerifiedIcon
} from '@mui/icons-material';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' 
  ? 'https://barebiologics-api-bdgjaefwc4h7d9gu.eastus2-01.azurewebsites.net/api'
  : 'http://localhost:8080/api');

const DonorEligibilityDialog = ({ open, onClose, donor, onSuccess }) => {
  const [eligibilityData, setEligibilityData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(null); // Track which doc is uploading
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const fileInputRef = useRef({});

  // Fetch eligibility status when dialog opens
  useEffect(() => {
    if (open && donor) {
      fetchEligibilityStatus();
    }
  }, [open, donor]);

  const fetchEligibilityStatus = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/donors/${donor.id}/eligibility-status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEligibilityData(data.data);
      } else {
        throw new Error('Failed to fetch eligibility status');
      }
    } catch (err) {
      console.error('Error fetching eligibility status:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (documentType) => {
    const fileInput = fileInputRef.current[documentType];
    if (fileInput) {
      fileInput.click();
    } else {
      console.error(`File input ref not found for document type: ${documentType}`);
      setError(`File input not initialized for ${documentType}. Please refresh and try again.`);
    }
  };

  const handleFileUpload = async (documentType, event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(documentType);
      setError('');
      setSuccessMessage('');

      const token = localStorage.getItem('authToken');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(
        `${API_BASE_URL}/donors/${donor.id}/documents/${documentType}/upload`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to upload document');
      }

      setSuccessMessage(`${data.data.fileName} uploaded successfully`);
      fetchEligibilityStatus();
    } catch (err) {
      console.error('Error uploading document:', err);
      setError(err.message);
    } finally {
      setUploading(null);
      // Reset file input
      if (fileInputRef.current[documentType]) {
        fileInputRef.current[documentType].value = '';
      }
    }
  };

  const handleCompleteEligibility = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/donors/${donor.id}/complete-eligibility`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to complete eligibility');
      }

      onSuccess(data.data);
    } catch (err) {
      console.error('Error completing eligibility:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEligibilityData(null);
    setError('');
    setSuccessMessage('');
    onClose();
  };

  if (!donor) return null;

  const progressPercent = eligibilityData 
    ? (eligibilityData.completedCount / eligibilityData.totalRequired) * 100 
    : 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VerifiedIcon color="warning" />
          <Typography variant="h5" fontWeight="bold">Donor Eligibility</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {donor.donorId} - {donor.mareName}
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        )}

        {/* Progress Card */}
        <Card sx={{ mb: 3, bgcolor: eligibilityData?.isComplete ? 'success.light' : 'warning.light' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Status: {eligibilityData?.isComplete ? 'Complete' : 'Eligibility Pending'}
              </Typography>
              <Chip 
                label={`${eligibilityData?.completedCount || 0}/${eligibilityData?.totalRequired || 10} documents`}
                color={eligibilityData?.isComplete ? 'success' : 'warning'}
                size="small"
              />
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={progressPercent} 
              sx={{ 
                height: 10, 
                borderRadius: 5,
                bgcolor: 'rgba(255,255,255,0.5)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: eligibilityData?.isComplete ? 'success.main' : 'warning.main'
                }
              }}
            />
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
              {Math.round(progressPercent)}% complete
            </Typography>
          </CardContent>
        </Card>

        {/* Document List */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
          Required Documents
        </Typography>

        {loading && !eligibilityData ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List sx={{ bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
            {eligibilityData?.documents?.map((doc, index) => (
              <React.Fragment key={doc.type}>
                {index > 0 && <Divider />}
                <ListItem sx={{ py: 1.5 }}>
                  <ListItemIcon>
                    {doc.complete ? (
                      <CheckIcon color="success" />
                    ) : (
                      <WarningIcon color="warning" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1" fontWeight={doc.complete ? 'normal' : 'bold'}>
                        {index + 1}. {doc.name}
                      </Typography>
                    }
                    secondary={
                      doc.complete ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <DocIcon fontSize="small" color="action" />
                          <Typography variant="caption" color="success.main">
                            {doc.fileName || 'Uploaded'}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="warning.main">
                          Pending upload
                        </Typography>
                      )
                    }
                  />
                  <ListItemSecondaryAction>
                    {uploading === doc.type ? (
                      <CircularProgress size={24} />
                    ) : (
                      <>
                        <input
                          type="file"
                          ref={(el) => fileInputRef.current[doc.type] = el}
                          style={{ display: 'none' }}
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={(e) => handleFileUpload(doc.type, e)}
                        />
                        <Button
                          size="small"
                          variant={doc.complete ? 'outlined' : 'contained'}
                          startIcon={doc.complete ? <UploadIcon /> : <UploadIcon />}
                          onClick={() => handleFileSelect(doc.type)}
                        >
                          {doc.complete ? 'Replace' : 'Upload'}
                        </Button>
                      </>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}

        {/* Info Alert */}
        <Alert severity="info" sx={{ mt: 2 }}>
          All {eligibilityData?.totalRequired || 10} documents must be uploaded before eligibility can be marked as complete.
          Once complete, vials can be released from quarantine to available inventory.
        </Alert>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Close
        </Button>
        <Button 
          onClick={handleCompleteEligibility} 
          variant="contained"
          disabled={!eligibilityData?.isComplete || loading}
          startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
          color="success"
        >
          {loading ? 'Processing...' : 'Complete Eligibility'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DonorEligibilityDialog;

